import { useState, useRef, useEffect, createContext, useContext } from "react";
import { HelpCircle } from "lucide-react";

// Context to manage which tooltip is open (ensures only one at a time)
const TooltipContext = createContext(null);

function TooltipProvider({ children }) {
  const [openId, setOpenId] = useState(null);
  return (
    <TooltipContext.Provider value={{ openId, setOpenId }}>
      {children}
    </TooltipContext.Provider>
  );
}

/**
 * MobileTooltip - A tooltip component that works on both desktop (hover) and mobile (tap)
 * - Desktop: Shows on hover
 * - Mobile: Shows on tap, closes when tapping outside or on another tooltip
 */
function MobileTooltip({ content, id, children }) {
  const context = useContext(TooltipContext);
  const [localOpen, setLocalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  const tooltipId = useRef(id || Math.random().toString(36).substr(2, 9));

  // Use context if available, otherwise local state
  const isOpen = context ? context.openId === tooltipId.current : localOpen;
  const setIsOpen = (open) => {
    if (context) {
      context.setOpenId(open ? tooltipId.current : null);
    } else {
      setLocalOpen(open);
    }
  };

  // Detect if device is mobile/touch
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia("(max-width: 768px)").matches || "ontouchstart" in window);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close tooltip when clicking outside (mobile only)
  useEffect(() => {
    if (!isMobile) return;
    
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, isMobile]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const handleClick = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(!isOpen);
    }
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className="w-5 h-5 bg-[#E7E5E4] rounded-full flex items-center justify-center text-[#57534E] hover:bg-[#d6d3d1] transition-colors focus:outline-none focus:ring-2 focus:ring-[#BFA15F] focus:ring-offset-1"
        data-testid="tooltip-trigger"
      >
        {children || <HelpCircle className="w-3 h-3" />}
      </button>

      {isOpen && (
        <div
          ref={tooltipRef}
          role="tooltip"
          className="absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 max-w-[calc(100vw-2rem)] bg-[#0F0F10] text-white p-3 rounded-md shadow-lg animate-in fade-in-0 zoom-in-95 duration-200"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-testid="tooltip-content"
        >
          <p className="text-sm leading-relaxed">{content}</p>
          {/* Arrow */}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-[#0F0F10]" />
        </div>
      )}
    </div>
  );
}

export { MobileTooltip, TooltipProvider };
export default MobileTooltip;
